/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable react/jsx-closing-tag-location */
/* eslint-disable react/jsx-one-expression-per-line */
import { useTranslate } from 'hooks/useTranslate';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Page from 'modules/_shared/components/Pages/Page';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import PageWithTabs from 'modules/_shared/components/Pages/PageWithTabs';
import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import PartnerInvestment from 'modules/partners/components/PartnerInvestment';
import PartnerPersonal from 'modules/partners/components/PartnerPersonal';
import PartnerSociety from 'modules/partners/components/PartnerSociety';
import PartnerAsideWrapper from 'modules/partners/components/PartnerAsideWrapper';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import PartnerObservations from 'modules/partners/components/PartnerObservations';
import { getPartnerDetailsConfigByCorporateId } from 'modules/partners/utils/getPartnerDetailsConfigByCorporateId';

import screens from 'constants/screens';
import menuTypes from 'constants/menuTypes';

import { formatNumber } from 'utils/formats';
import { hasUnlimitedVersion } from 'utils/hasUnlimitedVersion';
import { isPlatformASG, isPlatformAXA } from 'utils/showDetails';

import { useGetActualSociety } from 'hooks/useGetActualSociety';

import PartnerDocumentsView from 'modules/partners/components/PartnerDocumentsView';
import { usePartnerDetail } from 'modules/partners/pages/PartnerDetail/hooks/usePartnerDetail';
import { getPartnerProfileBadges } from 'modules/partners/components/PartnerSociety/components/utils/getPartnerProfileBadges';
import PartnerDetailSendEmailButton from 'modules/partners/components/PartnerDetailSendEmailButton';
import { PartnerDocumentsViewProvider } from 'modules/partners/components/PartnerDocumentsView/context/PartnerDocumentsViewContext';
import { PartnerOperationsProvider } from 'modules/partners/components/PartnerOperations/context/PartnerOperationsContext';
import PartnerOperations from 'modules/partners/components/PartnerOperations';

import PartnerDetailInviteButton from '../../components/PartnerDetailInviteButton';

import './PartnerDetail.scss';
import { useGetPartnerCategories } from './hooks/useGetPartnerCategories';

const PAGES_WITH_ASIDE = [0, 1];

const PartnerDetail = ({ initialPage = 0 }) => {
  const { t } = useTranslate();
  const { societyId, partnerId, pageId } = useParams();

  const usePartnerDetailsResult = usePartnerDetail({
    partnerId,
    societyId,
  });

  const {
    partner: actualPartner,
    incorporationDate,
    inversion,
  } = usePartnerDetailsResult.data;

  const { isLoading } = usePartnerDetailsResult;

  const { fetchPartnerData } = usePartnerDetailsResult.actions;

  const { categories } = useGetPartnerCategories({
    partner: actualPartner,
  }).data;

  const { isAdmin } = useGetPartnersSectionPermissions().data;

  const user = useSelector((state) => state.user);
  const actualSociety = useSelector((state) => state.society?.actualSociety);
  const holdingClasses = useSelector((state) => state.holdingClasses);

  const [, setPage] = useState(+pageId || initialPage);
  const [isCardVisible, setIsCardVisible] = useState(true);
  const [screenShowAside] = useState(screens.XL);

  const showTransactionsTab = useMemo(() => {
    if (!isPlatformAXA && !isPlatformASG) {
      return true;
    }
    return !!isAdmin;
  }, [isAdmin]);

  const { showSharingButton, showSendEmailButton, showIncorporationDate } =
    getPartnerDetailsConfigByCorporateId(actualSociety);

  const showDocumentsTab =
    !(actualSociety?.configuration?.hasPartnerDocumentsTabDisabled ?? false) ||
    isAdmin;

  const handleClick = () => {
    setIsCardVisible(!isCardVisible);
  };

  const pageDescription = useMemo(
    () => (
      <ul className="list-inline">
        <li>
          {t('PartnerNo')}:&nbsp;
          <span className="text-base fw-bold">
            {formatNumber(actualPartner?.partnerNumber)}
          </span>
        </li>

        {showIncorporationDate && (
          <li>
            {t('Since')}:&nbsp;
            <span className="text-base fw-bold">{`${
              incorporationDate || '-'
            }`}</span>
          </li>
        )}
      </ul>
    ),
    [t, actualPartner, incorporationDate, showIncorporationDate]
  );

  const corporateId = actualSociety?.corporateId || '';
  const badges = useMemo(
    () =>
      getPartnerProfileBadges({
        corporateId,
        partner: actualPartner,
        categories,
        partnerRepresentants: [],
        isAdmin,
        t,
      }),
    [corporateId, actualPartner, categories, isAdmin, t]
  );

  const pages = useMemo(
    () => [
      <PartnerSociety
        key="PartnerSociety"
        partner={actualPartner}
        society={actualSociety}
        inversion={inversion}
        badges={badges}
        isLoading={isLoading}
        fetchPartnerData={fetchPartnerData}
      />,
      <PartnerPersonal
        key="PartnerPersonal"
        incorporationDate={incorporationDate}
        society={actualSociety}
        partner={actualPartner}
      />,

      <PartnerOperationsProvider partner={actualPartner}>
        <PartnerOperations />
      </PartnerOperationsProvider>,

      <PartnerDocumentsViewProvider partner={actualPartner}>
        <PartnerDocumentsView />
      </PartnerDocumentsViewProvider>,

      <PartnerInvestment
        key="PartnerInvestment"
        society={actualSociety}
        partner={actualPartner}
        inversion={inversion}
        setPage={setPage}
        holdingClasses={holdingClasses}
      />,
      <PartnerObservations partnerId={partnerId} />,
    ],
    [
      actualPartner,
      actualSociety,
      inversion,
      badges,
      isLoading,
      fetchPartnerData,
      incorporationDate,
      holdingClasses,
      partnerId,
    ]
  );

  const onChangePage = (newPage) => {
    setPage(newPage);
    if (PAGES_WITH_ASIDE.includes(newPage)) {
      setIsCardVisible(true);
    } else {
      setIsCardVisible(false);
    }
  };

  useGetMenu(menuTypes.EXTENDED);
  useGetActualSociety(societyId);

  return (
    <>
      {actualPartner && (
        <Page id="partner-detail">
          <PageListHeader
            subTitle={t('Partner')}
            subTitleSuffix={actualPartner?.name || ''}
            description={pageDescription}
            actions={[
              showSharingButton || isAdmin ? (
                <PartnerDetailInviteButton partner={actualPartner} />
              ) : null,
              showSendEmailButton && isAdmin ? (
                <PartnerDetailSendEmailButton partner={actualPartner} />
              ) : null,
              <GoBackButton />,
            ].filter(Boolean)}
          />

          <PageWithTabs
            tabs={[
              {
                key: 'summary-tab',
                icon: 'ni-user-circle',
                title: t('Partner'),
                content: pages[0],
              },
              {
                key: 'personal-tab',
                icon: 'ni-list',
                title: t('Data'),
                content: pages[1],
              },
              showTransactionsTab
                ? {
                    key: 'transactions-tab',
                    icon: 'ni-repeat',
                    title: t('Transactions'),
                    content: pages[2],
                  }
                : null,
              showDocumentsTab
                ? {
                    key: 'documents-tab',
                    icon: 'ni-file-text',
                    title: t('Documents'),
                    content: pages[3],
                  }
                : null,
              isAdmin
                ? {
                    key: 'investment-tab',
                    icon: 'ni-invest',
                    title: t('ROI'),
                    content: pages[4],
                  }
                : null,
              isAdmin
                ? {
                    key: 'observations-tab',
                    icon: 'ni-note-add',
                    title: t('Notes'),
                    content: pages[5],
                  }
                : null,
            ].filter(Boolean)}
            asideContent={
              <PartnerAsideWrapper
                isCardVisible={isCardVisible}
                screenShowAside={screenShowAside}
                handleClick={handleClick}
                data={{
                  user,
                  isAdmin,
                  isLoading,
                  actualPartner,
                  actualSociety,
                  incorporationDate,
                  setIsCardVisible,
                  setPage,
                  inversion,
                  badges,
                }}
              />
            }
            onChangeTab={onChangePage}
            className={
              hasUnlimitedVersion() ? 'standard-version' : 'stretch-version'
            }
          />
        </Page>
      )}

      {isLoading && <CustomLoading />}
    </>
  );
};

export default PartnerDetail;
